import { serviceAreas } from "@/data/serviceAreas";

export const SITE_URL = "https://apexav.com";

export type Crumb = {
  /** Human-readable label shown in the trail and used in BreadcrumbList. */
  name: string;
  /** Site-relative path, e.g. "/services/home-theater". Undefined for the current page. */
  path?: string;
};

/** Routes that are private tools / app surfaces — no public breadcrumbs there. */
const PRIVATE_PREFIXES = [
  "/tools",
  "/inventory",
  "/estimator",
  "/blueprint-counter",
  "/sold-homes",
  "/unsubscribe",
  "/email",
  "/api",
  "/lovable",
];

export function isBreadcrumbHidden(pathname: string): boolean {
  const p = normalize(pathname);
  if (p === "/") return true;
  return PRIVATE_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

/** Labels for top-level section paths. */
const SECTION_LABELS: Record<string, string> = {
  "/services": "Services",
  "/service-areas": "Service Areas",
  "/blog": "Blog",
  "/about": "About",
  "/contact": "Contact",
  "/gallery": "Gallery",
  "/sitemap": "Sitemap",
  "/privacy": "Privacy Policy",
  "/cookies": "Cookie Policy",
  "/home-theater": "Home Theater",
  "/home-theater-near-me": "Home Theater Near Me",
  "/lighting": "Lighting Control",
  "/automated-shades": "Automated Shades",
  "/distributed-audio": "Distributed Audio",
};

/** Labels for individual service detail pages. */
const SERVICE_LABELS: Record<string, string> = {
  "home-theater": "Custom Home Theater",
  "dolby-atmos-home-theater": "Dolby Atmos Home Theater",
  "smart-home": "Smart Home Automation",
  "whole-home-audio": "Whole-Home Audio",
  "hidden-audio": "Hidden & Invisible Audio",
  "whole-home-video": "Whole-Home Video",
  outdoor: "Outdoor Entertainment",
  "climate-control": "Smart Climate Control",
  lighting: "Smart Lighting",
  shading: "Motorized Shading",
  security: "Security & Surveillance",
  networking: "Networking & Wi-Fi",
  conference: "Conference & Office AV",
  prewire: "New Construction Prewire",
  retrofit: "Retrofit & Cable Fishing",
  "tv-mounting": "TV Mounting",
  "voice-control": "Voice Control",
};

/** Labels for the neighborhood landing-page service segment. */
const LOCAL_SERVICE_LABELS: Record<string, string> = {
  "home-theater-installation": "Home Theater Installation",
  "smart-home-installation": "Smart Home Installation",
};

const AREA_NAME_BY_SLUG = new Map(serviceAreas.map((a) => [a.slug, a.name]));

function normalize(pathname: string): string {
  if (!pathname) return "/";
  const stripped = pathname.split("?")[0].split("#")[0];
  if (stripped.length > 1 && stripped.endsWith("/")) return stripped.slice(0, -1);
  return stripped || "/";
}

function titleize(slug: string): string {
  return slug
    .split("-")
    .map((w) => (w.length <= 2 && w !== "tv" ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ")
    .replace(/\bTv\b/g, "TV")
    .replace(/\bAv\b/g, "AV");
}

/**
 * Build the breadcrumb trail for a pathname. The final crumb has no `path`
 * because it represents the current page.
 *
 * `lastLabel` overrides the label of the deepest crumb (used for dynamic
 * routes such as blog posts where the slug is not a good label).
 */
export function breadcrumbTrail(pathname: string, lastLabel?: string): Crumb[] {
  const path = normalize(pathname);
  const crumbs: Crumb[] = [{ name: "Home", path: "/" }];
  if (path === "/") return crumbs;

  const segments = path.slice(1).split("/");
  const [first, second] = segments;

  if (first === "services") {
    crumbs.push({ name: "Services", path: "/services" });
    if (second) crumbs.push({ name: SERVICE_LABELS[second] ?? titleize(second) });
  } else if (first === "service-areas") {
    crumbs.push({ name: "Service Areas", path: "/service-areas" });
    if (second) crumbs.push({ name: AREA_NAME_BY_SLUG.get(second) ?? titleize(second) });
  } else if (first === "blog") {
    crumbs.push({ name: "Blog", path: "/blog" });
    if (second) crumbs.push({ name: lastLabel ?? titleize(second) });
  } else if (second && LOCAL_SERVICE_LABELS[second] && AREA_NAME_BY_SLUG.has(first)) {
    // Neighborhood landing pages: /castle-rock/home-theater-installation
    crumbs.push({ name: "Service Areas", path: "/service-areas" });
    crumbs.push({
      name: AREA_NAME_BY_SLUG.get(first)!,
      path: `/service-areas/${first}`,
    });
    crumbs.push({ name: LOCAL_SERVICE_LABELS[second] });
  } else {
    // Single-segment or unmapped pages
    const label = SECTION_LABELS[`/${first}`] ?? titleize(first);
    if (segments.length === 1) {
      crumbs.push({ name: lastLabel ?? label });
    } else {
      crumbs.push({ name: label, path: `/${first}` });
      crumbs.push({ name: lastLabel ?? titleize(segments.slice(1).join(" ")) });
    }
  }

  // Last crumb is the current page — never a link.
  const last = crumbs[crumbs.length - 1];
  if (last && crumbs.length > 1) delete last.path;
  if (lastLabel && crumbs.length > 1) crumbs[crumbs.length - 1].name = lastLabel;
  return crumbs;
}

/** BreadcrumbList JSON-LD node for a trail, with absolute URLs. */
export function breadcrumbListSchema(crumbs: Crumb[], pathname: string) {
  const currentPath = normalize(pathname);
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE_URL}${c.path ?? currentPath}`,
    })),
  };
}
